import { handleUpdateStudyInterprettingPhysician, STUDY_STATUS } from '@/entities/study';

import {
  generateFullName,
  showMessageError,
  showMessageInfo,
  showMessageSuccess,
} from '@/shared/lib';
import { useSetState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { t } from 'ttag';
import { EDIT_DRAWER_KEY } from '../../../../config/editStudyDetails';
import type { EditInterPhysicianProps, ReferringPhysicianOption } from '../editStudyDetails.types';

interface InterPhysicianState {
  interprettingPhysician: string | null;
  selectedInterprettingPhysician: any;
  isOpenConfirmModal: boolean;
  isInitiating: boolean;
  isSaving: boolean;
}

interface InterPhysicianStoreData {
  interprettingPhysician: string | undefined;
  selectedInterprettingPhysician: any;
}

/**
 * Drives the drawer that changes a study's interpreting physician. Re-seeds the selection and its
 * storeData baseline from the study whenever the drawer is open and the data changes, and wires the
 * physician select, the no-changes save guard, the save and the close handlers.
 *
 * @param props - The study data, open flag, interprettingPhysicians options, onClose, drawerKey and
 * the updateStudyData callback.
 * @returns An object carrying the drawer state and setter, the storeData baseline ref, the
 * aborted-study flag, and the change, save-guard, close and save handlers.
 */
export const useEditInterPhysicianDrawer = ({
  data,
  open,
  interprettingPhysicians,
  onClose,
  drawerKey,
  updateStudyData,
}: Pick<
  EditInterPhysicianProps,
  'data' | 'open' | 'interprettingPhysicians' | 'onClose' | 'drawerKey' | 'updateStudyData'
>) => {
  const [state, setState] = useSetState<InterPhysicianState>({
    interprettingPhysician: null,
    selectedInterprettingPhysician: null,
    isOpenConfirmModal: false,
    isInitiating: false,
    isSaving: false,
  });

  const storeData = React.useRef<InterPhysicianStoreData | null>(null);

  const isAbortedStudy = data?.status === STUDY_STATUS.ABORTED;

  /**
   * Records the interpreting physician picked in the study details edit drawer. Looks the chosen option up in the loaded interprettingPhysicians list and stores both the matched record and the option value in state.
   *
   * @param name - Name of the select field, unused.
   * @param option - Selected physician option whose value holds the physician id.
   * @returns Nothing.
   */
  const handleInterprettingPhysicianChange = (name: string, option: ReferringPhysicianOption) => {
    /**
     * The interpreting physician entry matching the chosen option. Found with a lodash lookup over the loaded interpreting physicians by comparing their value against the option value.
     *
     * @returns The matching physician option, or undefined when none matches.
     */
    const selectedInterprettingPhysician = _.find(
      interprettingPhysicians,
      (interprettingPhysician) => interprettingPhysician.value === option.value,
    );
    setState({
      selectedInterprettingPhysician,
      interprettingPhysician: option.value,
    });
  };

  /**
   * Seeds the interpreting-physician drawer from the study's info.interprettingPhysician. Builds a select option carrying the generated full name, id, phone1, nightPhone, fax and email into the storeData ref as the pristine baseline, then merges a deep clone of it into state and clears isInitiating.
   *
   * @returns Promise resolving once the baseline and the drawer state are populated.
   */
  const initiateState = async () => {
    setState({ isInitiating: true });
    const { info } = data || {};

    const { interprettingPhysician } = info || {};

    storeData.current = {
      interprettingPhysician: interprettingPhysician?.id,
      selectedInterprettingPhysician: {
        label: generateFullName(interprettingPhysician),
        title: generateFullName(interprettingPhysician),
        value: interprettingPhysician?.id,
        name: generateFullName(interprettingPhysician),
        phone1: interprettingPhysician?.contact?.phone1,
        nightPhone: interprettingPhysician?.contact?.nightPhone,
        fax: interprettingPhysician?.contact?.fax,
        email: interprettingPhysician?.email,
      },
    };

    setState({
      ..._.cloneDeep(storeData.current),
      isInitiating: false,
    });
  };

  /**
   * Guards the interpreting-physician save behind a change check. Shows an information message when the selected physician still equals the baseline held in storeData, otherwise opens the confirmation modal.
   *
   * @returns Nothing.
   */
  const handleClickSave = () => {
    if (state.interprettingPhysician === storeData.current?.interprettingPhysician) {
      showMessageInfo({ content: t`No changes were made.` });
      return;
    }
    setState({ isOpenConfirmModal: true });
  };

  /**
   * Commits the chosen interpreting physician to the study. Sends studyId and the selected physician through handleUpdateStudyInterprettingPhysician, closes the confirm modal, shows a success message and pushes the returned physician to updateStudyData under the interpreting-physician drawer key, while failures are logged and reported and isSaving is always cleared.
   *
   * @returns Promise resolving once the update finished and the saving flag was cleared.
   */
  const onSave = async () => {
    setState({ isSaving: true });
    try {
      const input = {
        studyId: data?.id,
        interprettingPhysician: state.interprettingPhysician as string,
      };
      const result = await handleUpdateStudyInterprettingPhysician(input);
      setState({ isOpenConfirmModal: false });
      const { interprettingPhysician } = result?.study?.info || {};
      showMessageSuccess({ content: t`Interpreting physician updated successfully.` });
      updateStudyData(
        {
          interprettingPhysician: interprettingPhysician
            ? {
                id: interprettingPhysician.id,
                firstName: interprettingPhysician.firstName ?? '',
                lastName: interprettingPhysician.lastName ?? '',
                email: interprettingPhysician.email,
                npi: interprettingPhysician.npi ?? '',
                contact: {
                  fax: interprettingPhysician.contact?.fax ?? null,
                  nightPhone: interprettingPhysician.contact?.nightPhone ?? null,
                  phone1: interprettingPhysician.contact?.phone1 ?? null,
                },
              }
            : undefined,
        },
        EDIT_DRAWER_KEY.INTEREPTING_PHYSICIAN,
      );
    } catch (error) {
      console.error('Failed to update study interpretting physician: ', error);
      showMessageError({ content: t`Failed to update interpreting physician.` });
    } finally {
      setState({ isSaving: false });
    }
  };
  /**
   * Closes the interpreting-physician edit drawer. Forwards the drawer key to the onClose prop.
   *
   * @returns Nothing.
   */
  const handleOnClose = () => {
    if (onClose && drawerKey) {
      onClose(drawerKey);
    }
  };

  useEffect(() => {
    if (open) {
      initiateState();
    }
  }, [data, open]);

  return {
    state,
    setState,
    storeData,
    isAbortedStudy,
    handleInterprettingPhysicianChange,
    handleClickSave,
    handleOnClose,
    onSave,
  };
};
